import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

/**
 * OtpInput — best-practice one-time-code input.
 *
 * ONE real <input> (invisible, stretched over the whole row) renders as six
 * visual "slots". Why a single input instead of six boxes:
 *   • iOS/Android `autocomplete="one-time-code"` autofill only fills ONE
 *     field reliably — six boxes break the keyboard suggestion.
 *   • Native paste, Ctrl+A, arrow keys and backspace all "just work".
 *   • `inputMode="numeric"` + `pattern="[0-9]*"` → numeric keypad.
 *   • 16px font on the real input prevents iOS focus-zoom.
 * (Refs: web.dev/articles/sms-otp-form, Twilio OTP best practices, input-otp.)
 *
 * Editing model — SELECTION MIRRORING (not append-only):
 * the native input's caret/selection is tracked and painted onto the slots.
 *   • Click a slot → that digit gets selected → typing replaces it.
 *   • Ctrl+A → all slots highlight → Backspace clears / typing restarts.
 *   • Arrow keys move the caret between slots natively.
 *
 * Props:
 *   length      — number of digits (default 6)
 *   value       — current code string (controlled)
 *   onChange    — (code) => void
 *   onComplete  — (code) => void, fired when all digits are present
 *   disabled    — disable input
 *   error       — tint slots red (invalid code)
 *   autoFocus   — focus on mount
 *
 * Imperative handle: ref.focus(), ref.clear()
 */
const OtpInput = forwardRef(function OtpInput(
  { length = 6, value = '', onChange, onComplete, disabled = false, error = false, autoFocus = false },
  ref
) {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [sel, setSel] = useState([0, 0]); // mirrored native selection [start, end]

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    clear: () => onChange?.(''),
  }), [onChange]);

  const digits = value.slice(0, length).split('');

  // Mirror the native selection into state so slots can paint caret/highlight.
  const syncSel = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    setSel([el.selectionStart ?? 0, el.selectionEnd ?? 0]);
  }, []);

  const handleChange = (e) => {
    const code = e.target.value.replace(/\D/g, '').slice(0, length);
    onChange?.(code);
    if (code.length === length && code !== value) onComplete?.(code);
    requestAnimationFrame(syncSel);
  };

  // The invisible input covers the slots, so raw clicks land on it at
  // arbitrary text positions. Map the click X to a slot: a filled slot gets
  // SELECTED (typing replaces that digit); past the last digit → caret at end.
  const placeCaretFromPointer = (e) => {
    const el = inputRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = Math.max(0, Math.min(length - 1, Math.floor(((e.clientX - rect.left) / rect.width) * length)));
    if (idx < value.length) el.setSelectionRange(idx, idx + 1);
    else el.setSelectionRange(value.length, value.length);
    syncSel();
  };

  const [selStart, selEnd] = sel;
  const hasRange = selEnd > selStart;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={length}
        value={value}
        onChange={handleChange}
        onSelect={syncSel}
        onKeyUp={syncSel}
        onMouseUp={placeCaretFromPointer}
        onFocus={() => { setFocused(true); requestAnimationFrame(syncSel); }}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={`${length}-digit one-time code`}
        className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-text"
        style={{ fontSize: 16, caretColor: 'transparent' }}
      />
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${length}, 1fr)` }} aria-hidden="true">
        {Array.from({ length }).map((_, i) => {
          const digit = digits[i] || '';
          // Selected range (e.g. Ctrl+A, or a clicked digit) highlights slots.
          const inRange = focused && !disabled && hasRange && i >= selStart && i < selEnd;
          // Collapsed caret sits before position selStart → paint that slot
          // (clamped to the last slot when the code is full).
          const caretSlot = Math.min(selStart, length - 1);
          const isCaret = focused && !disabled && !hasRange && i === caretSlot;
          const isActive = isCaret || inRange;
          const borderColor = error ? '#9a564a' : isActive ? 'var(--teal)' : digit ? '#b9ceca' : 'var(--border)';
          return (
            <div
              key={i}
              className="flex items-center justify-center font-bold select-none transition-all"
              style={{
                height: 48, // ≥44px touch target
                borderRadius: 'var(--r-md)',
                border: `1.5px solid ${borderColor}`,
                background: disabled ? '#f8f5ef' : inRange ? 'rgba(20,95,95,0.08)' : '#fff',
                color: error ? '#9a564a' : '#08111f',
                fontSize: 18,
                boxShadow: isActive ? 'var(--sh-focus-soft)' : 'none',
                transitionDuration: 'var(--dur-fast)',
              }}
            >
              {digit || (isCaret ? (
                <span
                  className="cs-otp-caret"
                  style={{ width: 1.5, height: 20, background: 'var(--teal)', display: 'inline-block' }}
                />
              ) : '')}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default OtpInput;
